import React from 'react';
import PropTypes from 'prop-types';

import {Box, Stack, TextField, Typography, Divider} from 'views/components';
import {UploadFileToS3, SecureFileGrid, Translate, translate} from 'views/module';
import {CloudUpload, DeleteForever} from 'views/icons';

const ProfileEdit = (props) => {
  const {bioQuestions, fields, setFields, getSignedFileUrl} = props;

  React.useEffect(() => {
    const resolvedFields = {
      shortBio: bioQuestions.shortBio || '',
      personalBio: bioQuestions.personalBio || '',
      caregiverMotivation: bioQuestions.caregiverMotivation || '',
      profilePhoto: Array.isArray(bioQuestions.profilePhoto) ? [...bioQuestions.profilePhoto] : [],
    };
    setFields(resolvedFields);
  }, [bioQuestions, setFields]);

  const handleChange = (field) => (value) => {
    setFields((prev) => ({...prev, [field]: value}));
  };

  const profilePhotoFiles = fields.profilePhoto || [];
  const filesExist = profilePhotoFiles.length > 0;

  const getProfilePhotoSignedUrl = async (file) => {
    return getSignedFileUrl('profilePhoto', file, true, (fileUrl) => {
      const newFile = {
        name: file.name,
        type: file.type,
        src: fileUrl,
      };

      setFields((prev) => {
        const previousFiles = prev.profilePhoto || [];
        return {
          ...prev,
          profilePhoto: [...previousFiles, newFile],
        };
      });
    });
  };

  const handleDelete = (fileToRemove) => {
    setFields((prev) => {
      const previousFiles = prev.profilePhoto || [];
      return {
        ...prev,
        profilePhoto: previousFiles.filter((file) => file.src !== fileToRemove.src),
      };
    });
  };

  return (
    <Stack spacing={2}>
      <TextField
        label={translate('caregiver.CaregiverBiography.Profile.ShortBio')}
        value={fields.shortBio}
        onChange={handleChange('shortBio')}
        multiline
        minRows={2}
        fullWidth
      />
      <TextField
        label={translate('caregiver.CaregiverBiography.Profile.PersonalBio')}
        value={fields.personalBio}
        onChange={handleChange('personalBio')}
        multiline
        minRows={4}
        fullWidth
      />
      <TextField
        label={translate('caregiver.CaregiverBiography.Profile.CaregiverMotivation')}
        value={fields.caregiverMotivation}
        onChange={handleChange('caregiverMotivation')}
        multiline
        minRows={4}
        fullWidth
      />
      <Stack spacing={1}>
        <Typography variant="subtitle2" color="text.secondary">
          <Translate expression='caregiver.CaregiverBiography.Profile.ProfilePhoto' />
        </Typography>
        {
          filesExist && (
            <>
              <Divider />
              <SecureFileGrid
                files={profilePhotoFiles}
                menuItems={[
                  {
                    Icon: DeleteForever,
                    text: 'Delete',
                    handler: handleDelete,
                  },
                ]}
              />
            </>
          )
        }
        <UploadFileToS3 getSignedUrl={getProfilePhotoSignedUrl}>
          <Box
            sx={(theme) => ({
              'width': '100%',
              'minHeight': 200,
              'borderRadius': '16px',
              'cursor': 'pointer',
              'position': 'relative',
              'display': 'flex',
              'alignItems': 'center',
              'justifyContent': 'center',
              'padding': theme.spacing(4),
              'border': `2px dashed ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.25)'}`,
              'background': theme.palette.mode === 'light' ?
                'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(245, 247, 250, 0.95))' :
                theme.palette.background.paper,
              'boxShadow': theme.palette.mode === 'light' ?
                '0 12px 24px rgba(31, 41, 55, 0.08)' :
                '0 16px 32px rgba(0, 0, 0, 0.45)',
              'transition': 'transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease',
              '&:hover': {
                borderColor: theme.palette.primary.main,
                boxShadow: theme.palette.mode === 'light' ?
                  '0 18px 38px rgba(31, 41, 55, 0.12)' :
                  '0 20px 40px rgba(0, 0, 0, 0.55)',
                transform: 'translateY(-2px)',
                background: theme.palette.mode === 'light' ?
                  'linear-gradient(135deg, rgba(255, 255, 255, 1), rgba(245, 247, 250, 1))' :
                  theme.palette.background.default,
              },
              '&:focus-within': {
                borderColor: theme.palette.primary.main,
                boxShadow: theme.palette.mode === 'light' ?
                  `0 0 0 4px ${theme.palette.primary.main}33` :
                  `0 0 0 4px ${theme.palette.primary.main}4d`,
              },
            })}
          >
            <Stack spacing={1.5} alignItems='center' textAlign='center'>
              <Box
                sx={(theme) => ({
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme.palette.mode === 'light' ?
                    `${theme.palette.primary.main}14` :
                    `${theme.palette.primary.main}26`,
                  color: theme.palette.primary.main,
                })}
              >
                <CloudUpload fontSize='large' />
              </Box>
              <Stack spacing={0.5} alignItems='center'>
                <Typography variant='subtitle1' color='text.primary'>
                  <Translate expression='caregiver.CaregiverBiography.Profile.UploadFile' />
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </UploadFileToS3>
      </Stack>
    </Stack>
  );
};

ProfileEdit.propTypes = {
  bioQuestions: PropTypes.object.isRequired,
  fields: PropTypes.shape({
    shortBio: PropTypes.string,
    personalBio: PropTypes.string,
    caregiverMotivation: PropTypes.string,
    profilePhoto: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      src: PropTypes.string.isRequired,
    })),
  }).isRequired,
  setFields: PropTypes.func.isRequired,
  getSignedFileUrl: PropTypes.func.isRequired,
};

export default React.memo(ProfileEdit);


